import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";

import { templates } from "@reactivated";

// The card shape as serialized server-side (see server/cms/product_card.py).
type ProductCardData =
    templates.ProductListPageTemplate["categories"][number]["products"][number];

type IProps = Immutable<{
    product: ProductCardData;
    // BEM base class applied to the card, also used to derive element classes
    // (e.g. `${className}__image`).
    className?: string;
    // Whether to render the pre-title callout. When false, the pre-title block
    // is omitted and the image alt falls back to "".
    showPreTitle?: boolean;
    // Whether to render the aux-copy description block.
    showAuxCopy?: boolean;
}>;

export const ProductCard = (props: IProps): React.JSX.Element => {
    const {
        product,
        className,
        showPreTitle = true,
        showAuxCopy = true,
    } = props;
    const cls = (suffix: string) => (className ? ` ${className}${suffix}` : "");

    return (
        <div
            className={`card product-card product-card--tempurpedic${
                className ? ` ${className}` : ""
            }`}
            data-product={product.id}
        >
            {product.product_schema_json_ld && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: product.product_schema_json_ld,
                    }}
                />
            )}
            <a href={product.link}>
                <img
                    src={product.image_url}
                    className={`card__image${cls("__image")} responsive-img`}
                    alt={showPreTitle ? product.pre_title : ""}
                    loading="lazy"
                    decoding="async"
                />
            </a>

            <div className={`card__content${cls("__content")}`}>
                {showPreTitle && product.pre_title && (
                    <div
                        className={`product-card__pre-title${cls("__pre-title")}`}
                    >
                        {product.pre_title}
                    </div>
                )}

                <div
                    className={`card__title product-card__title product-card__title--tempurpedic${cls(
                        "__title",
                    )}`}
                >
                    <a href={product.link}>{product.title}</a>
                </div>

                {product.category_name && (
                    <div
                        className={`product-card__category product-card__category--tempurpedic${cls(
                            "__category",
                        )}`}
                    >
                        {product.category_name}
                    </div>
                )}

                <a href={product.reviews_url}>
                    <StarRating
                        rating={product.rating}
                        // A zero review count must hide the number entirely;
                        // StarRating renders any non-null number, so map 0 to null.
                        number={product.num_reviews || null}
                        className="product-card__rating"
                    />
                </a>

                {showAuxCopy && (
                    <div
                        className={`product-card__aux-copy${cls("__aux-copy")}`}
                        dangerouslySetInnerHTML={{
                            __html: product.description_html,
                        }}
                    />
                )}

                {product.price_exists && (
                    <div
                        className={`product-card__price--tempurpedic${cls(
                            "__price",
                        )}`}
                    >
                        {product.price_retail ? (
                            <span>
                                <span className="product-card__price__retail">
                                    {product.price_retail}
                                </span>
                                <span className="product-card__price__actual configurator__price__actual--highlight product-card__price__actual--tempurpedic">
                                    {product.price_actual}
                                </span>
                            </span>
                        ) : (
                            <span>
                                <span className="product-card__price__actual">
                                    {product.price_actual}
                                </span>
                            </span>
                        )}
                        {product.per_month && (
                            <a
                                href={product.finance_url}
                                className={`product-card__installment${cls(
                                    "__installment",
                                )}`}
                            >
                                or {product.per_month}/mo. for{" "}
                                {product.per_month_term_length} months
                                <sup>2</sup>
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
